/**
 * Pure sign layout: an atlas of exact-aspect regions inside one 1024x512 texture plus contain-fit
 * text blocks. Text measurement is injected so the same code runs under vitest without a canvas.
 *
 * Founder feedback 2026-10-09 ("signs cut off"): every sign plane samples a region whose aspect
 * ratio is EXACTLY the plane's (no centred crop of a differently shaped region), the layout is
 * computed inside that region with padding on every side, text shrinks and wraps (<= 2 lines on
 * banners, <= 3 on rooftop boards) and only falls back to an ellipsis at the minimum size.
 */
export const SIGN_TEXTURE = { width: 1024, height: 512 } as const;
/** Space between atlas regions so mipmaps never bleed one region's ink into another. */
export const ATLAS_GUTTER = 12;

export interface Region {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface UvRect {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

export type SignStyle = "stacked" | "inline";

/** Wide planes (banners) put the monogram tile left of the name; squarer boards stack it above. */
export function styleForAspect(aspect: number): SignStyle {
  return aspect >= 2 ? "inline" : "stacked";
}

/** Line limits per style (docs: banners <= 2 lines, rooftop boards <= 3 lines). */
export const MAX_LINES: Record<SignStyle, number> = { inline: 2, stacked: 3 };
export const MIN_FONT_PX: Record<SignStyle, number> = { inline: 14, stacked: 18 };

/** Babylon UVs for a canvas region: v = 1 at the canvas top. */
export function regionUv(region: Region, texW: number = SIGN_TEXTURE.width, texH: number = SIGN_TEXTURE.height): UvRect {
  return {
    u0: region.x / texW,
    u1: (region.x + region.w) / texW,
    v1: 1 - region.y / texH,
    v0: 1 - (region.y + region.h) / texH,
  };
}

/** Crop a region (centred) so its aspect ratio matches a plane's width/height exactly. */
export function cropToAspect(region: Region, aspect: number): Region {
  const current = region.w / region.h;
  if (Math.abs(current - aspect) < 1e-9) return { ...region };
  if (current > aspect) {
    const w = region.h * aspect;
    return { x: region.x + (region.w - w) / 2, y: region.y, w, h: region.h };
  }
  const h = region.w / aspect;
  return { x: region.x, y: region.y + (region.h - h) / 2, w: region.w, h };
}

export interface SlotSpec {
  key: string;
  /** Plane size in metres (only the ratio matters). */
  width: number;
  height: number;
}

export interface AtlasSlot {
  key: string;
  aspect: number;
  style: SignStyle;
  region: Region;
  uv: UvRect;
}

export interface SignAtlas {
  width: number;
  height: number;
  slots: AtlasSlot[];
}

/**
 * Pack one exact-aspect region per slot into the texture: slots go into 1..3 equal columns
 * (shortest column first, squarer slots first); the fewest columns that fit wins, which gives the
 * largest regions. If even three columns overflow, the column width shrinks until they fit.
 */
export function buildSignAtlas(specs: readonly SlotSpec[], texW: number = SIGN_TEXTURE.width, texH: number = SIGN_TEXTURE.height, gutter = ATLAS_GUTTER): SignAtlas {
  const items = specs.map((s) => ({ key: s.key, aspect: s.width / s.height }));
  // Squarer (taller) slots first so they claim a column of their own.
  const order = [...items].sort((a, b) => a.aspect - b.aspect);
  const tryColumns = (cols: number, colW: number): AtlasSlot[] | null => {
    const heights = new Array<number>(cols).fill(gutter);
    const placed: AtlasSlot[] = [];
    for (const it of order) {
      let col = 0;
      for (let c = 1; c < cols; c++) if (heights[c]! < heights[col]!) col = c;
      const h = colW / it.aspect;
      const region: Region = { x: gutter + col * (colW + gutter), y: heights[col]!, w: colW, h };
      heights[col] = heights[col]! + h + gutter;
      if (heights[col]! > texH + 1e-6) return null;
      placed.push({ key: it.key, aspect: it.aspect, style: styleForAspect(it.aspect), region, uv: regionUv(region, texW, texH) });
    }
    return placed;
  };
  for (let cols = 1; cols <= 3; cols++) {
    const colW = (texW - gutter * (cols + 1)) / cols;
    const placed = tryColumns(cols, colW);
    if (placed) return { width: texW, height: texH, slots: items.map((it) => placed.find((p) => p.key === it.key)!) };
  }
  let colW = (texW - gutter * 4) / 3;
  for (let i = 0; i < 200; i++) {
    colW *= 0.97;
    const placed = tryColumns(3, colW);
    if (placed) return { width: texW, height: texH, slots: items.map((it) => placed.find((p) => p.key === it.key)!) };
  }
  throw new Error("sign atlas cannot fit the requested slots");
}

export function atlasSlot(atlas: SignAtlas, key: string): AtlasSlot {
  const s = atlas.slots.find((x) => x.key === key);
  if (!s) throw new Error(`no atlas slot ${key}`);
  return s;
}

/** True when a plane of this size shows the whole slot (same shape); otherwise `uvForPlane` crops the painted sign. */
export function planeFitsSlot(atlas: SignAtlas, key: string, width: number, height: number): boolean {
  return Math.abs(width / height - atlasSlot(atlas, key).aspect) < 1e-3;
}

/** UVs for a plane of the given size using a slot (crops only if the plane does not match the slot). */
export function uvForPlane(atlas: SignAtlas, key: string, width: number, height: number): UvRect {
  const slot = atlasSlot(atlas, key);
  const aspect = width / height;
  if (Math.abs(aspect - slot.aspect) < 1e-6) return slot.uv;
  return regionUv(cropToAspect(slot.region, aspect), atlas.width, atlas.height);
}

export type MeasureFn = (text: string, fontPx: number) => number;

export interface TextBlock {
  lines: string[];
  fontPx: number;
  lineHeight: number;
  /** Widest line in px. */
  width: number;
  height: number;
  /** True when the text did not fit even at the minimum size and was shortened with an ellipsis. */
  truncated: boolean;
}

export interface FitOptions {
  maxPx: number;
  minPx: number;
  maxLines: number;
  lineHeightFactor?: number;
}

function breakWord(measure: MeasureFn, word: string, maxWidth: number, px: number): string[] {
  const parts: string[] = [];
  let cur = "";
  for (const ch of word) {
    const next = cur + ch;
    if (cur.length > 0 && measure(next, px) > maxWidth) {
      parts.push(cur);
      cur = ch;
    } else {
      cur = next;
    }
  }
  if (cur) parts.push(cur);
  return parts;
}

/** Greedy word wrap at a fixed font size; over-long words are split by character. */
export function wrapLines(measure: MeasureFn, text: string, maxWidth: number, px: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = "";
  for (const word of words) {
    const candidate = cur ? `${cur} ${word}` : word;
    if (measure(candidate, px) <= maxWidth) {
      cur = candidate;
      continue;
    }
    if (cur) lines.push(cur);
    if (measure(word, px) <= maxWidth) {
      cur = word;
    } else {
      const pieces = breakWord(measure, word, maxWidth, px);
      lines.push(...pieces.slice(0, -1));
      cur = pieces[pieces.length - 1] ?? "";
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

/** True when a wrap had to split a word mid-way (we prefer a smaller font over broken words). */
function splitsWords(lines: readonly string[], text: string): boolean {
  const words = new Set(text.trim().split(/\s+/).filter(Boolean));
  return lines.some((l) => l.split(" ").some((w) => w && !words.has(w)));
}

/** Shorten `text` with an ellipsis until it fits `maxWidth` (always returns at least "…"). */
export function ellipsize(measure: MeasureFn, text: string, maxWidth: number, px: number): string {
  if (measure(text, px) <= maxWidth) return text;
  let t = text.trimEnd();
  while (t.length > 0 && measure(`${t}…`, px) > maxWidth) t = t.slice(0, -1).trimEnd();
  return `${t}…`;
}

/**
 * Contain-fit a text into a box: the largest font size (stepping down by 1 px) whose wrapped lines
 * fit the width, the line limit and the height, preferring sizes that do not split words. Falls
 * back to the minimum size with an ellipsis on the last line only when nothing else fits.
 */
export function fitTextBlock(measure: MeasureFn, text: string, box: { w: number; h: number }, opts: FitOptions): TextBlock {
  const lhf = opts.lineHeightFactor ?? 1.12;
  const clean = text.trim() || " ";
  let brokenFallback: TextBlock | null = null;
  for (let px = Math.floor(opts.maxPx); px >= opts.minPx; px -= 1) {
    const lines = wrapLines(measure, clean, box.w, px);
    const lineHeight = px * lhf;
    if (lines.length === 0 || lines.length > opts.maxLines) continue;
    if (lines.length * lineHeight > box.h) continue;
    const width = Math.max(...lines.map((l) => measure(l, px)));
    if (width > box.w) continue;
    const block: TextBlock = { lines, fontPx: px, lineHeight, width, height: lines.length * lineHeight, truncated: false };
    if (!splitsWords(lines, clean)) return block;
    brokenFallback ??= block;
  }
  if (brokenFallback) return brokenFallback;
  const px = opts.minPx;
  const lineHeight = px * lhf;
  const maxLines = Math.max(1, Math.min(opts.maxLines, Math.floor(box.h / lineHeight)));
  const wrapped = wrapLines(measure, clean, box.w, px);
  let lines = wrapped.slice(0, maxLines);
  if (wrapped.length > maxLines) {
    const rest = wrapped.slice(maxLines - 1).join(" ");
    lines = [...lines.slice(0, -1), ellipsize(measure, rest, box.w, px)];
  }
  if (lines.length === 0) lines = ["…"];
  const width = Math.max(...lines.map((l) => measure(l, px)));
  return { lines, fontPx: px, lineHeight, width, height: lines.length * lineHeight, truncated: wrapped.length > maxLines };
}

/** Largest font size (down to minPx) at which `text` is at most `maxWidth` wide and `maxHeight` tall. */
export function fitFontSize(measure: MeasureFn, text: string, maxWidth: number, maxHeight: number, maxPx: number, minPx: number): number {
  for (let px = Math.floor(Math.min(maxPx, maxHeight)); px >= minPx; px -= 1) {
    if (measure(text, px) <= maxWidth) return px;
  }
  return minPx;
}

export interface PlacedLine {
  text: string;
  /** Centre x of the line (textAlign "center"). */
  x: number;
  /** Centre y of the line box (textBaseline "middle"). */
  y: number;
}

/** Centre a text block in a box: one entry per line, all line boxes inside `box`. */
export function placeLines(block: TextBlock, box: Region): PlacedLine[] {
  const startY = box.y + (box.h - block.height) / 2 + block.lineHeight / 2;
  return block.lines.map((text, i) => ({ text, x: box.x + box.w / 2, y: startY + i * block.lineHeight }));
}

export interface SignLayout {
  region: Region;
  style: SignStyle;
  /** Inner padding kept clear on every side of the region. */
  pad: number;
  /** White monogram tile (square). */
  tile: Region;
  monogramPx: number;
  /** Box the business name is centred in. */
  nameBox: Region;
  name: TextBlock;
}

function monogramPx(measure: MeasureFn, monogram: string, tile: Region): number {
  return fitFontSize(measure, monogram, tile.w * 0.72, tile.h * 0.66, tile.h * 0.66, 8);
}

/**
 * M5: a sign with an approved raster logo draws it in the tile instead of the monogram. Rooftop boards show the logo
 * alone over the whole board (docs/01 priority 1: the logo is what people recognise; founder feedback 2026-10-13);
 * banners give the logo almost their full height and keep the business name beside it (ground-level identification).
 */
export const TILE_FRACTION_STACKED = { monogram: 0.38 } as const;
/**
 * Clear margin around a logo inside its tile (fraction of the tile side): the plain sign surface around it. Kept small
 * (founder feedback 2026-10-13: "the logo on the building is too small, needs to be more visible").
 */
export const LOGO_TILE_INSET = 0.035;

export interface LayoutOptions {
  /** The tile shows a raster logo (larger tile on rooftop boards). */
  logo?: boolean;
  /**
   * The rooftop board is square (stores with a logo): the stacked layout uses the centred square of its slot, which
   * is exactly what the square plane shows (uvForPlane crops the slot to the plane's aspect, centred).
   */
  square?: boolean;
}

/** The centred square of a region (side = the shorter edge). */
export function centredSquare(region: Region): Region {
  const side = Math.min(region.w, region.h);
  return { x: region.x + (region.w - side) / 2, y: region.y + (region.h - side) / 2, w: side, h: side };
}

const NO_TEXT: TextBlock = { lines: [], fontPx: 0, lineHeight: 0, width: 0, height: 0, truncated: false };

/**
 * Rooftop board: monogram tile on top, up to three lines of name below. With an approved logo the board shows the logo
 * alone, as large as the board allows (founder feedback 2026-10-13: "The logo needs to be larger"): a plain panel over
 * the whole board inside a thin brand-colour frame, the logo contain-fit in it. The business name stays on the
 * storefront banner below, in the panel and in the directory (docs/01 section 5: a readable label on selection and in
 * the directory; ground-level identification on tall buildings).
 */
export function layoutStacked(name: string, monogram: string, measure: MeasureFn, slotRegion: Region, opts: LayoutOptions = {}): SignLayout {
  const region = opts.square ? centredSquare(slotRegion) : slotRegion;
  if (opts.logo) {
    const framePad = Math.max(6, Math.round(Math.min(region.w, region.h) * 0.035));
    const tile: Region = { x: region.x + framePad, y: region.y + framePad, w: region.w - framePad * 2, h: region.h - framePad * 2 };
    return { region, style: "stacked", pad: framePad, tile, monogramPx: monogramPx(measure, monogram, tile), nameBox: { x: tile.x, y: tile.y + tile.h, w: tile.w, h: 0 }, name: NO_TEXT };
  }
  const pad = Math.max(8, Math.round(Math.min(region.w, region.h) * 0.07));
  const tileSize = Math.min(Math.round(region.h * TILE_FRACTION_STACKED.monogram), region.w - pad * 2);
  const tile: Region = { x: region.x + (region.w - tileSize) / 2, y: region.y + pad, w: tileSize, h: tileSize };
  const gap = Math.round(region.h * 0.04);
  const nameBox: Region = {
    x: region.x + pad,
    y: tile.y + tile.h + gap,
    w: region.w - pad * 2,
    h: region.y + region.h - pad - (tile.y + tile.h + gap),
  };
  const text = fitTextBlock(measure, name, nameBox, { maxPx: nameBox.h * 0.5, minPx: MIN_FONT_PX.stacked, maxLines: MAX_LINES.stacked });
  return { region, style: "stacked", pad, tile, monogramPx: monogramPx(measure, monogram, tile), nameBox, name: text };
}

/** Banner: monogram tile on the left, up to two lines of name to its right. */
export function layoutInline(name: string, monogram: string, measure: MeasureFn, region: Region, opts: LayoutOptions = {}): SignLayout {
  // A logo banner lets the logo use almost the full height.
  const pad = Math.max(opts.logo ? 4 : 6, Math.round(region.h * (opts.logo ? 0.05 : 0.1)));
  const tileSize = Math.min(region.h - pad * 2, Math.round(region.w * 0.3));
  const tile: Region = { x: region.x + pad, y: region.y + (region.h - tileSize) / 2, w: tileSize, h: tileSize };
  const gap = Math.round(region.h * 0.1);
  const nameBox: Region = {
    x: tile.x + tile.w + gap,
    y: region.y + pad,
    w: region.x + region.w - pad - (tile.x + tile.w + gap),
    h: region.h - pad * 2,
  };
  const text = fitTextBlock(measure, name, nameBox, { maxPx: nameBox.h * 0.62, minPx: MIN_FONT_PX.inline, maxLines: MAX_LINES.inline });
  return { region, style: "inline", pad, tile, monogramPx: monogramPx(measure, monogram, tile), nameBox, name: text };
}

export function layoutSlot(slot: AtlasSlot, name: string, monogram: string, measure: MeasureFn, opts: LayoutOptions = {}): SignLayout {
  return slot.style === "stacked" ? layoutStacked(name, monogram, measure, slot.region, opts) : layoutInline(name, monogram, measure, slot.region, opts);
}

/**
 * Where a logo of (iw, ih) pixels is drawn inside its tile: contain-fit (aspect preserved, never
 * distorted or cropped) within the tile minus LOGO_TILE_INSET on every side, centred.
 */
export function logoRect(tile: Region, iw: number, ih: number, inset: number = LOGO_TILE_INSET): Region {
  const m = Math.round(Math.min(tile.w, tile.h) * inset);
  const box: Region = { x: tile.x + m, y: tile.y + m, w: tile.w - m * 2, h: tile.h - m * 2 };
  if (!(iw > 0) || !(ih > 0)) return box;
  const s = Math.min(box.w / iw, box.h / ih);
  const w = iw * s;
  const h = ih * s;
  return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h };
}

/** Initials for a generated monogram when none is supplied (home wall signs, fallbacks). */
export function monogramFor(label: string): string {
  const words = label
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
  const letters = words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
  return letters || "T";
}
